import React, { useContext, FC, useEffect } from "react";
import { Route, Switch, Redirect } from "react-router-dom";
import Login from "../pages/auth/Login";
import SetPasswordPage from "../pages/Public/SetPasswordPage";
import RotaResponsePage from "../pages/Public/RotaResponsePage";
import PublicPartyPage from "../pages/Public/PublicPartyPage";
import Dashboard from "../pages/dashboard/DashboardPage";
import ServiceMembersPage from "../pages/services/ServiceMembersPage";
import ManageParty from "../pages/features/manage-party/PartyManagement";
import PublicRegistrationReviewPage from "../pages/features/manage-party/PublicRegistrationReviewPage";
import DuplicatePartiesPage from "../pages/features/manage-party/DuplicatePartiesPage";
import PartyIntegrityPage from "../pages/features/manage-party/PartyIntegrityPage";
import ArchivedPartiesReportPage from "../pages/reports/ArchivedPartiesReportPage";
import PlatformUserPage from "../pages/features/manage-tenant-user/ManageUsersPage";
import ServicesPage from "../pages/features/manage-service/ServicesPage";
import ServiceRotaPage from "../pages/features/manage-service/rota/ServiceRotaPage";
import ServiceRolesPage from "../pages/features/manage-service/roles/ServiceRolesPage";
import SessionReportPage from "../pages/features/manage-service/instances/SessionReportPage";
import TenantServiceCalendarPage from "../pages/features/manage-service/calendar/TenantServiceCalendarPage";
import SupplierInvoicesPage from "../pages/features/invoicing/SupplierInvoicesPage";
import RegistrationsPage from "../pages/features/manage-services-attendance/RegistrationPage";
import ManageAudience from "../pages/features/manage-communications/audience-management/ManageAudiencePage";
import ManageCampaign from "../pages/features/manage-communications/campaign-management/CampaignPage";
import CampaignReportPage from "../pages/features/manage-communications/campaign-management/CampaignReportPage";
import ConversationInboxPage from "../pages/features/manage-communications/conversations/ConversationInboxPage";
import WebhookManagementPage from "../pages/features/webhook-management/WebhookManagementPage";
import ContactsReportPage from "../pages/reports/ContactsReportPage";
import SessionSummaryReportPage from "../pages/reports/SessionSummaryReportPage";
import ReportsPage from "../pages/reports/ReportsPage";
import TenantAdministrationPage from "../pages/features/tenant-administration/TenantAdministrationPage";
import OrganisationPeoplePage from "../pages/features/tenant-administration/OrganisationPeoplePage";
import OrganisationDetailsPage from "../pages/features/tenant-administration/OrganisationDetailsPage";
import SystemOrganisationManagementPage from "../pages/features/tenant-administration/SystemOrganisationManagementPage";
import StaticDataManagementPage from "../pages/features/static-data/StaticDataManagementPage";
import CommunityDirectoryImportPage from "../pages/features/community-directory-import/CommunityDirectoryImportPage";
import { AuthContext } from "../context/AuthContext";
import { useLoading } from "../context/LoadingContext";
import PrivateRoute from "../app/PrivateRoute";
import { ToastContainer } from "react-toastify";
import {
  registerLoadingHandler,
  registerNotifyHandler,
} from "../api/apiClient";
import { toast } from "react-toastify";
import AppLayout from "./layout/AppLayout";
import { FEATURE_CODES } from "../constants/constants";

import "react-toastify/dist/ReactToastify.css";
import "../css/app.css";

const App: FC = () => {
  const authContext = useContext(AuthContext);
  if (!authContext) {
    throw new Error("AuthContext must be used within an AuthProvider");
  }

  const { isAuthenticated } = authContext;
  const { loading, setLoading } = useLoading();

  /* Register loading handler for API */
  useEffect(() => {
    registerLoadingHandler(setLoading);
  }, [setLoading]);

  useEffect(() => {
    registerNotifyHandler((type, message) => {
      if (type === "success") toast.success(message);
      if (type === "error") toast.error(message);
      if (type === "info") toast.info(message);
    });
  }, []);

  return (
    <>
      <ToastContainer
        position="top-right"
        autoClose={3000}
        newestOnTop
        closeOnClick
        pauseOnHover
        draggable
      />

      {loading && (
        <div className="spinner-overlay">
          <div className="spinner" />
        </div>
      )}

      <AppLayout>
        <Switch>
          <Route
            exact
            path="/"
            render={() =>
              isAuthenticated ? (
                <Redirect to="/dashboard" />
              ) : (
                <Redirect to="/login" />
              )
            }
          />
          <Route path="/set-password" component={SetPasswordPage} />
          <Route path="/rota-response/:token" component={RotaResponsePage} />
          <Route
            path="/login"
            render={() =>
              isAuthenticated ? <Redirect to="/dashboard" /> : <Login />
            }
          />
          <Route exact path="/register" component={PublicPartyPage} />
          <Route path="/profile/:token" component={PublicPartyPage} />

          <PrivateRoute path="/dashboard" component={Dashboard} />
          <PrivateRoute
            exact
            path="/calendar"
            component={TenantServiceCalendarPage}
            feature={FEATURE_CODES.SERVICE_CALENDAR}
          />
          <PrivateRoute path="/invoices/:invoiceId?" component={SupplierInvoicesPage} />
          <PrivateRoute
            path="/services/:serviceId/members"
            component={ServiceMembersPage}
            feature={FEATURE_CODES.SERVICE_MEMBERS}
          />
          <PrivateRoute
            path="/services/:serviceId/rota"
            component={ServiceRotaPage}
            feature={FEATURE_CODES.SERVICE_ROTA}
          />
          <PrivateRoute
            path="/services/:serviceId/roles"
            component={ServiceRolesPage}
            feature={FEATURE_CODES.SERVICE_TEAM_ROLES}
          />
          <PrivateRoute
            path="/services/:serviceId/session-reports"
            component={SessionReportPage}
            feature={FEATURE_CODES.SERVICE_SESSION_REPORTS}
          />

          <PrivateRoute
            path="/admin/manage-users"
            component={PlatformUserPage}
          />
          <PrivateRoute
            path="/admin/manage-services"
            component={ServicesPage}
            feature={FEATURE_CODES.SERVICE_DETAILS}
          />
          <PrivateRoute
            path="/admin/public-registrations"
            component={PublicRegistrationReviewPage}
            feature={FEATURE_CODES.SERVICE_REGISTRATION}
          />
          <PrivateRoute
            path="/admin/duplicate-parties"
            component={DuplicatePartiesPage}
            access="admin"
          />
          <PrivateRoute
            path="/admin/community-directory-import"
            component={CommunityDirectoryImportPage}
            access="systemAdmin"
          />
          <PrivateRoute
            path="/admin/manage-tenants"
            component={TenantAdministrationPage}
            access="admin"
          />
          <PrivateRoute
            path="/admin/system/organisations"
            component={SystemOrganisationManagementPage}
            access="systemAdmin"
          />
          <PrivateRoute path="/admin/system/party-integrity" component={PartyIntegrityPage} access="systemAdmin" />
          <PrivateRoute
            path="/admin/system/static-data"
            component={StaticDataManagementPage}
            access="systemAdmin"
          />
          <PrivateRoute
            path="/admin/organisation/details"
            component={OrganisationDetailsPage}
            access="admin"
          />
          <PrivateRoute
            path={[
              "/admin/organisation/people",
              "/admin/organisation/system-users",
              "/admin/organisation/members",
            ]}
            component={OrganisationPeoplePage}
            access="admin"
          />
          <PrivateRoute
            path="/admin/webhooks"
            component={WebhookManagementPage}
            access="systemAdmin"
          />
          <PrivateRoute exact path="/reports" component={ReportsPage} access="admin" />
          <PrivateRoute path="/reports/archived-parties" component={ArchivedPartiesReportPage} access="admin" />
          <PrivateRoute
            path="/reports/contacts"
            component={ContactsReportPage}
            access="admin"
          />
          <PrivateRoute
            path="/reports/session-summary"
            component={SessionSummaryReportPage}
            access="admin"
            feature={FEATURE_CODES.SERVICE_SESSION_REPORTS}
          />
          <PrivateRoute path="/party/:partyId" component={ManageParty} />
          <PrivateRoute path="/manage-party" component={ManageParty} />
          {/* <PrivateRoute path="/create-user" component={CreateUser} /> */}
          <PrivateRoute
            path="/party-service-registration"
            component={RegistrationsPage}
            feature={FEATURE_CODES.SERVICE_ATTENDANCE_CHECK_IN}
          />

          <PrivateRoute path="/manage-audiences" component={ManageAudience} feature={FEATURE_CODES.MESSAGING_AUDIENCES_COMMS} />
          <PrivateRoute path="/conversations" component={ConversationInboxPage} feature={FEATURE_CODES.MESSAGING_AUDIENCES_COMMS} />
          <PrivateRoute
            path="/campaigns/:campaignId/report"
            component={CampaignReportPage}
            feature={FEATURE_CODES.MESSAGING_AUDIENCES_COMMS}
          />
          <PrivateRoute
            path={["/manage-campaigns", "/manage-campaign"]}
            component={ManageCampaign}
            feature={FEATURE_CODES.MESSAGING_AUDIENCES_COMMS}
          />

          {/* <PrivateRoute path="/event-history" component={EventHistory} />
          <PrivateRoute
            path="/event-registration/:eventId?/:userId?"
            component={EventRegistration}
          /> */}
          {/* <PrivateRoute
            path="/event-attendees/:eventId"
            component={EventAttendees}
          /> */}

          <Redirect to="/" />
        </Switch>
      </AppLayout>
    </>
  );
};

export default App;
